import { CancelTokenSource } from 'axios';
import {
  HealthCardDataDto,
  OnboardPatientSearchRequest,
  PatientDataResponseDto,
  PatientDocumentRequest,
  PatientDto,
  PatientEmailFormDto,
  PatientEmailRequest,
  PatientEmailSearchRequest,
  PatientEmailSendStatus,
  PatientHealthCardSearchRequest,
  PatientInfoDto,
  PatientPhysiotherapyReportDto,
  PatientPrescriptionEmailDto,
  PatientPrintLabelDto,
  PatientReportEmailRequest,
  PatientResponse,
  PatientSaveDocumentRequest,
  PatientSearchRequest,
  PatientSynopsisReportDto,
  PdfResponse,
  PrescriptionDocumentRequest,
  SendPatientEmailsRequest,
  UnableToWorkEmailRequest,
  UpsertPatientDto
} from 'ts-types/api.types';
import axios from './http';

const {REACT_APP_API_BASE_URL: baseUrl} = process.env;
const apiUri = `${baseUrl}/api/patients`;

export const searchPatients = (
    source: CancelTokenSource,
    searchData: Partial<PatientSearchRequest>): Promise<Array<PatientDto>> => {

  return axios
      .post(`${apiUri}/search`, searchData, {cancelToken: source.token})
      .then((response) => response.data);
};

export const searchOnboardPatients = (
    source: CancelTokenSource,
    searchData: Partial<OnboardPatientSearchRequest>): Promise<PatientResponse[]> => {

  return axios
      .post(`${apiUri}/patientCentralSearch`, searchData, {cancelToken: source.token})
      .then((response) => response.data);
};

export const healthCardSearchPatients = (
    searchData: Partial<PatientHealthCardSearchRequest>,
    source: CancelTokenSource): Promise<Array<PatientDto>> => {

  return axios
      .post(`${apiUri}/healthCardSearch`, searchData, {cancelToken: source.token})
      .then((response) => response.data);
};

export const findPatientById = (source: CancelTokenSource, patientId: number): Promise<UpsertPatientDto> => {
  return axios
      .get(`${apiUri}/${patientId}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const refreshPatientData = (source: CancelTokenSource, patientId: number): Promise<PatientDto> => {
  return axios
      .get(`${apiUri}/refresh/${patientId}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getPatientForPatientCentral = (
    patientId: number, source: CancelTokenSource)
    : Promise<PatientInfoDto> => {

  return axios
      .get(`${apiUri}/patient-central/${patientId}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getPatientData = (patientId: number, source: CancelTokenSource): Promise<PatientDataResponseDto> => {
  return axios
      .get(`${apiUri}/patient-data/${patientId}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const checkPatientHealthCardData = (healthCardData: Partial<HealthCardDataDto>,
                                           source: CancelTokenSource): Promise<boolean> => {
  return axios
      .post(`${apiUri}/check-health-card`, healthCardData, {cancelToken: source.token})
      .then((response) => response.data);
};

export const updatePatientDataFromHealthCard = (healthCardData: Partial<HealthCardDataDto>,
                                                source: CancelTokenSource): Promise<PatientDataResponseDto> => {
  return axios
      .post(`${apiUri}/update-from-health-card`, healthCardData, {cancelToken: source.token})
      .then((response) => response.data);
};

export const addPatient = (
    patient: Partial<UpsertPatientDto>,
    source: CancelTokenSource): Promise<PatientDto> => {

  return axios
      .post(`${apiUri}/add`, patient, {cancelToken: source.token})
      .then((response) => response.data);
};

export const editPatient = (
    patientId: number,
    patient: Partial<UpsertPatientDto>,
    mandateId: number,
    source: CancelTokenSource): Promise<PatientDto> => {
  return axios
      .put(`${apiUri}/${patientId}`,
          patient,
          {params: {mandateId: mandateId}, cancelToken: source.token})
      .then((response) => response.data);
};

export const deletePatient = (
    source: CancelTokenSource,
    patientId?: number): Promise<PatientDto> => {

  return axios
      .delete(`${apiUri}/${patientId}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const fastPrintPatientDocuments = (
    source: CancelTokenSource, patientId: number): Promise<Array<PatientPrintLabelDto>> => {

  return axios
      .get(`${apiUri}/fastPatientDocument/${patientId}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const unableToWorkPatientDocument = (
    patientDocumentRequest: PatientDocumentRequest,
    source: CancelTokenSource): Promise<PatientEmailFormDto[]> => {

  return axios
      .post(`${apiUri}/unableToWorkPatientDocument`, patientDocumentRequest, {cancelToken: source.token})
      .then((response) => response.data);
};

export const sendUnableToWorkEmailToPatient = (
    emailRequest: UnableToWorkEmailRequest, source: CancelTokenSource): Promise<String> => {

  return axios
      .post(`${apiUri}/unableToWorkPatientDocument/email`, emailRequest, {cancelToken: source.token})
      .then((response) => response.data);
};

export const searchPatientEmails = (
    searchRequest: Partial<PatientEmailSearchRequest>,
    source: CancelTokenSource)
    : Promise<PatientPrescriptionEmailDto[]> => {

  return axios
      .post(`${apiUri}/emails/search`, searchRequest, {cancelToken: source.token})
      .then(response => response.data);
}

export const addPatientEmail = (
    request: PatientEmailRequest,
    source: CancelTokenSource
): Promise<PatientPrescriptionEmailDto> => {

  return axios
      .post(`${apiUri}/emails`,
          request, {cancelToken: source.token})
      .then((response) => response.data);
}

export const addPatientReportEmail = (
    request: Partial<PatientSaveDocumentRequest>,
    source: CancelTokenSource
): Promise<PatientPrescriptionEmailDto> => {

  return axios
      .post(`${apiUri}/report-emails`,
          request, {cancelToken: source.token})
      .then((response) => response.data);
}

export const editPatientEmail = (
    patientEmailId: number,
    request: PatientEmailRequest,
    source: CancelTokenSource): Promise<PatientPrescriptionEmailDto> => {

  return axios
      .put(`${apiUri}/emails/${patientEmailId}`,
          request,
          {cancelToken: source.token})
      .then((response) => response.data);
}

export const cancelPatientEmail = (
    patientEmailId: number,
    source: CancelTokenSource): Promise<PatientPrescriptionEmailDto> => {

  return axios.put(`${apiUri}/emails/${patientEmailId}/cancel`, {cancelToken: source.token})
      .then((response) => response.data)
}

export const sendPatientEmails = (
    request: Partial<SendPatientEmailsRequest>,
    source: CancelTokenSource)
    : Promise<number> => {

  return axios
      .post(`${apiUri}/emails/send`,
          request, {cancelToken: source.token})
      .then(response => response.data);
}

export const getPrescriptionDocumentFromPendingEmailsList = (
    prescriptionDocumentRequest: PrescriptionDocumentRequest,
    sendStatus: PatientEmailSendStatus,
    source: CancelTokenSource)
    : Promise<PatientEmailFormDto[]> => {

  return axios
      .post(`${apiUri}/get-prescription-document`, prescriptionDocumentRequest,
          {params: {sendStatus: sendStatus}, cancelToken: source.token})
      .then((response) => response.data);
};

export const getReportDocumentFromPendingEmailsList = (
    documentId: number,
    sendStatus: PatientEmailSendStatus,
    source: CancelTokenSource)
    : Promise<PatientEmailFormDto[]> => {

  return axios
      .post(`${apiUri}/get-report-document/${documentId}`, {},
          {params: {sendStatus: sendStatus}, cancelToken: source.token})
      .then((response) => response.data);
};

export const prescriptionDocument = (
    prescriptionDocumentRequest: PrescriptionDocumentRequest, source: CancelTokenSource)
    : Promise<PatientEmailFormDto[]> => {

  return axios
      .post(`${apiUri}/prescriptionDocument`, prescriptionDocumentRequest, {cancelToken: source.token})
      .then((response) => response.data);
};

export const vaccinationCardDocument = (
    patientId: number, checkIn: boolean, source: CancelTokenSource)
    : Promise<PdfResponse> => {

  return axios
      .get(`${apiUri}/${patientId}/vaccinationCardDocument`,
          {params: {checkIn: checkIn}, cancelToken: source.token})
      .then((response) => response.data)
      .catch(error => {
        throw error;
      });
};

export const vaccinationCardMandateDocument = (
    patientId: number, source: CancelTokenSource)
    : Promise<PdfResponse> => {

  return axios
      .get(`${apiUri}/${patientId}/vaccinationCardMandateDocument`, {cancelToken: source.token})
      .then((response) => response.data)
      .catch(error => {
        throw error;
      });
};

export const generatePatientSynopsisReport = (
    patientId: number,
    synopsisReportDto: Partial<PatientSynopsisReportDto>,
    source: CancelTokenSource): Promise<PdfResponse> => {

  return axios
      .post(`${apiUri}/${patientId}/synopsis-report`,
          synopsisReportDto, {cancelToken: source.token})
      .then(response => response.data);
};

export const generatePatientPhysiotherapyReport = (
    patientId: number,
    physiotherapyReportDto: Partial<PatientPhysiotherapyReportDto>,
    source: CancelTokenSource): Promise<PdfResponse> => {

  return axios
      .post(`${apiUri}/${patientId}/physiotherapy-report`,
          physiotherapyReportDto, {cancelToken: source.token})
      .then(response => response.data);
};

export const addPatientReportToCdr = (
    request: Partial<PatientSaveDocumentRequest>,
    source: CancelTokenSource): Promise<number> => {

  return axios
      .post(`${apiUri}/save-report`,
          request, {cancelToken: source.token})
      .then(response => response.data);
};

export const sendPatientReportEmailToDoctor = (
    emailRequest: PatientReportEmailRequest, source: CancelTokenSource): Promise<String> => {

  return axios
      .post(`${apiUri}/send-report/email`, emailRequest, {cancelToken: source.token})
      .then((response) => response.data);
};
